import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
    useCallback,
} from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from './AuthContext';
import { useBilling } from './BillingContext';
import { sortBookingsHelper } from '@/utils/dateUtils';
import { hasUnlimitedBookings } from '@/lib/planFeatures';
import { createBookingService, updateBookingService, deleteBookingService } from '@/services/events/bookingService';
import { insertPerformanceService, deletePerformanceService } from '@/services/events/performanceService';

interface EventsContextType {
    [key: string]: any;
}

const EventsContext = createContext<EventsContextType | null>(null);

export const useEvents = () => {
    const context = useContext(EventsContext);
    if (!context) {
        throw new Error('useEvents must be used within an EventsProvider');
    }
    return context;
};

export const EventsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { user, organization, isAuthenticated } = useAuth();
    const { subscription } = useBilling();


    const [categories, setCategories] = useState<any[]>([]);
    const [shopCategories, setShopCategories] = useState<any[]>([]);
    const [performances, setPerformances] = useState<any[]>([]);
    const [bookings, setBookings] = useState<any[]>([]);
    const [busySlots, setBusySlots] = useState<any[]>([]);
    const [shiftChanges, setShiftChanges] = useState<any[]>([]);
    const [reviews, setReviews] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchBookings = async () => {
        if (!organization?.id) return;
        try {
            // PostgREST limita ogni risposta a max ~1000 righe: pagino con .range()
            // così i saloni con molte prenotazioni caricano tutto lo storico + agenda futura.
            const PAGE_SIZE = 1000;
            let from = 0;
            let bookingsData: any[] = [];
            // eslint-disable-next-line no-constant-condition
            while (true) {
                const { data: page, error: pageError } = await supabase
                    .from('Bookings')
                    .select('*, reviews:Reviews(comment, rating, created_at), client:Customers(full_name)')
                    .eq('shop_id', organization.id)
                    .order('date', { ascending: false })
                    .order('time', { ascending: false })
                    .range(from, from + PAGE_SIZE - 1);
                if (pageError) throw pageError;
                const batch = page || [];
                bookingsData = bookingsData.concat(batch);
                if (batch.length < PAGE_SIZE) break;
                from += PAGE_SIZE;
            }

            const reviews = (bookingsData || [])
                .filter((b: any) => b.reviews && b.client)
                .map((booking: any) => {
                    return {
                        id: booking.id,
                        author: booking.client.full_name,
                        comment: booking.reviews.comment,
                        rating: booking.reviews.rating,
                        created_at: booking.reviews.created_at,
                    };
                });

            setReviews(reviews);
            setBookings(bookingsData || []);
        } catch (error) {
            console.error('Errore fetch bookings:', error);
        }
    };

    const loadInitialData = useCallback(async () => {
        if (!organization?.id) return;

        try {
            setIsLoading(true);


            // Categories
            const { data: categoriesData, error: categoriesError } = await supabase
                .from('Categories').select('*');
            if (categoriesError) throw categoriesError;
            setCategories(categoriesData || []);

            // ShopCategories
            const { data: shopCategoriesData, error: shopCategoriesError } = await supabase
                .from('ShopCategories').select('*').eq('shop_id', organization.id);
            if (shopCategoriesError) throw shopCategoriesError;
            setShopCategories(shopCategoriesData || []);

            // Performances
            const { data: performancesData, error: performancesError } = await supabase
                .from('Performances').select('*').eq('shop_id', organization.id);
            if (performancesError) throw performancesError;
            setPerformances(performancesData || []);

            // Bookings
            await fetchBookings();

            // BusySlots
            const { data: busySlotsData, error: busySlotsError } = await supabase
                .from('BusySlots').select('*').eq('shop_id', organization.id);
            if (busySlotsError) throw busySlotsError;
            setBusySlots(busySlotsData || []);

            // ShiftChanges
            const { data: shiftChangesData, error: shiftChangesError } = await supabase
                .from('ShiftChanges').select('*').eq('shop_id', organization.id);
            if (shiftChangesError) throw shiftChangesError;
            setShiftChanges(shiftChangesData || []);

        } catch (error) {
            console.error('Errore nel caricamento dei dati:', error);
        } finally {
            setIsLoading(false);
        }
    }, [organization?.id, user]);

    useEffect(() => {
        if (isAuthenticated && organization?.id) {
            loadInitialData();

            // Polling ogni 30 secondi (30000 ms)
            const interval = setInterval(() => {
                fetchBookings();
            }, 30000);

            return () => clearInterval(interval);
        } else {
            setIsLoading(false);
        }
    }, [isAuthenticated, organization?.id, loadInitialData]);

    // REALTIME (DA AGGIORNARE)
    useEffect(() => {
        if (!isAuthenticated || !organization?.id) return;

        const channel = supabase
            .channel('bookings_changes')
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'Bookings',
                    filter: `shop_id=eq.${organization.id}`,
                },
                (payload) => {
                    if (payload.eventType === 'INSERT') {
                        const newBooking = payload.new;
                        setBookings((prev) => {
                            if (prev.find((b) => b.id === newBooking.id)) return prev;
                            const newBookings = [...prev, newBooking];
                            return sortBookingsHelper(newBookings);
                        });
                    } else if (payload.eventType === 'UPDATE') {
                        const updatedBooking = payload.new;
                        setBookings((prev) => {
                            const newBookings = prev.map((b) => b.id === updatedBooking.id ? updatedBooking : b);
                            return sortBookingsHelper(newBookings);
                        });
                    } else if (payload.eventType === 'DELETE') {
                        const deletedId = payload.old.id;
                        setBookings((prev) => prev.filter((b) => b.id !== deletedId));
                    }
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [isAuthenticated, organization?.id]);

    // --- CRUD ---

    const createBooking = async (bookingData: any) => {
        const result = await createBookingService({
            bookingData,
            // Tutti i piani nuovi (pro/ai/enterprise) sono senza tetto; il cap 200/mese
            // resta solo per il legacy standard.
            isPro: hasUnlimitedBookings(subscription?.plan_id),
            currentBookings: bookings
        });

        if (result.success && result.data) {
            // Aggiornamento locale (se realtime tarda)
            setBookings((prev) => sortBookingsHelper([...prev, result.data]));
        }

        // Ritorna anche il booking creato (id) per chi deve collegarci altri record (es. sconto).
        return { success: result.success, error: result.error, data: result.data };
    };

    const updateBooking = async (oldBookingId: number | string, bookingData: any) => {
        const result = await updateBookingService(oldBookingId, bookingData);

        if (!result.success) {
            console.error("Errore update:", result.error);
            return result;
        }

        // Aggiorna lo stato locale
        setBookings((prev) => {
            const updatedBookings = prev.map((booking) =>
                booking.id == oldBookingId ? { ...booking, ...bookingData } : booking
            );
            return sortBookingsHelper(updatedBookings);
        });

        return { success: true, error: '' };
    };

    const deleteBooking = async (bookingId: number | string) => {
        const result = await deleteBookingService(bookingId);

        if (!result.success) {
            return result;
        }

        setBookings((prev) => prev.filter((b) => b.id != bookingId));
        return { success: true };
    };

    // --- HELPER FUNZIONI ---

    const getBookingById = (id: number | string) => bookings.find((b) => b.id == id);
    const getPerformanceById = (id: number | string) => performances.find((p) => p.id == id);

    const checkServiceAvailability = (performanceId: number | string) => {
        const perf = performances.find((p) => p.id == performanceId);
        return perf ? perf.is_active : true;
    };

    const getServicesByHairdresser = (hairdresserId: string) => {
        return performances.filter((p) => p.hairdresser_id == hairdresserId && p.is_active !== false);
    };

    const getServicesByOrganization = (shopId: string) => {
        return performances.filter((p) => p.shop_id === shopId && p.is_active !== false);
    };

    const getPerformancesByHairdresser = (hairdresserId: string) => {
        return performances.filter((p) => p.hairdresser_id == hairdresserId);
    };

    const getPerformanceByHairdresser = (hairdresserId: string, performanceId: string) => {
        return performances.find((p) =>
            p.hairdresser_id == hairdresserId &&
            p.id == performanceId &&
            p.is_active !== false
        );
    };

    const getBookingPrice = (booking: any) => {
        const p = performances.find((perf) => perf.id === booking.performance_id);
        const basePrice = p?.price || 0;
        const adjustment = Number(booking.price_adjustment) || 0;
        return basePrice + adjustment;
    };


    const createBusySlot = async (busySlotData: any) => {
        const { data, error } = await supabase.from('BusySlots').insert([busySlotData]).select().single();
        if (error) return { success: false, error: error.message };
        setBusySlots((prev) => [...prev, data]);
        return { success: true, data };
    };

    const deleteBusySlot = async (busySlotId: number) => {
        const { error } = await supabase.from('BusySlots').delete().eq('id', busySlotId);
        if (error) return { success: false, error: error.message };
        setBusySlots((prev) => prev.filter((s) => s.id !== busySlotId));
        return { success: true };
    };

    const createShiftChange = async (shiftChangeData: any) => {
        const { data, error } = await supabase.from('ShiftChanges').insert([shiftChangeData]).select().single();
        if (error) return { success: false, error: error.message };
        setShiftChanges((prev) => [...prev, data]);
        return { success: true, data };
    };

    const deleteShiftChange = async (id: string) => {
        const { error } = await supabase.from('ShiftChanges').delete().eq('id', id);
        if (error) return { success: false, error: error.message };
        setShiftChanges((prev) => prev.filter((s) => s.id !== id));
        return { success: true };
    };

    const insertPerformance = async (performanceData: any) => {
        const result = await insertPerformanceService(performanceData);
        if (!result.success) return result;

        const finalPerf = result.data;

        if (result.isUpdate) {
            setPerformances((prev) => prev.map(p => p.id === finalPerf.id ? finalPerf : p));
        } else {
            setPerformances((prev) => [...prev, finalPerf]);
        }

        // Aggiornamento categorie (frontend)
        if (finalPerf && finalPerf.category_id) {
            const hasCategory = shopCategories.some(sc => sc.category_id === finalPerf.category_id);
            if (!hasCategory) {
                const fullCat = categories.find(c => c.id === finalPerf.category_id);
                if (fullCat) {
                    setShopCategories(prev => [...prev, { ...fullCat, category_id: fullCat.id }]);
                }
            }
        }

        return { success: true };
    };

    const updatePerformance = async (id: number, data: any) => {
        const { data: updated, error } = await supabase.from('Performances').update(data).eq('id', id).select().single();
        if (error) return { success: false, error: error.message };
        setPerformances((prev) => prev.map(p => p.id === id ? updated : p));
        return { success: true };
    };

    const deletePerformance = async (id: number) => {
        const perfToDelete = performances.find(p => p.id === id);
        const hasBookings = bookings.some(b => b.performance_id === id);

        const result = await deletePerformanceService(id, hasBookings);
        if (!result.success) return result;

        if (result.isSoftDelete && result.data) {
            setPerformances((prev) => prev.map(p => p.id === id ? result.data : p));
        } else {
            setPerformances((prev) => prev.filter(p => p.id !== id));
        }

        // Aggiornamento categorie (frontend)
        if (perfToDelete && perfToDelete.category_id) {
            const remaining = performances.filter(p =>
                p.id !== id && // Escludi quella eliminata
                p.is_active !== false && // Solo attive
                p.category_id === perfToDelete.category_id
            );

            if (remaining.length === 0) {
                setShopCategories(prev => prev.filter(sc => sc.category_id !== perfToDelete.category_id));
            }
        }

        return { success: true };
    };

    const value: EventsContextType = {

        categories,
        shopCategories,
        performances,
        bookings,
        busySlots,
        reviews,
        isLoading,

        getServicesByHairdresser,

        createBooking,
        updateBooking,
        deleteBooking,

        getPerformanceById,
        getBookingById,
        getServicesByOrganization,
        getPerformancesByHairdresser,
        getPerformanceByHairdresser,
        updatePerformance,
        deletePerformance,
        insertPerformance,
        getBookingPrice,
        createBusySlot,
        deleteBusySlot,
        shiftChanges,
        createShiftChange,
        deleteShiftChange,
        checkServiceAvailability,
    };

    return (
        <EventsContext.Provider value={value}>
            {children}
        </EventsContext.Provider>
    );
};